import type * as THREE from 'three'
import {
  createAssemblyMateDocument,
  toAssemblyMateDocumentJSON,
  type AssemblyMateDocumentJSON,
} from '../../assembly/document'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyDatumExtensionInput,
  PuppyPackageDocumentType,
} from './types'
import { PUPPY_PACKAGE_VERSION } from './types'
import { toMatrixTuple } from '../../imported-model/loaders/assembly-node-utils'
import {
  createPuppyDatumExtension,
  writePuppyDatumExtensionToAssemblyExtensions,
} from './puppyDatumExtension'

/**
 * 装配文档创建所需的可变上下文。
 */
export interface PuppyAssemblyDocumentBuildContext {
  /** 装配节点列表。*/
  assemblyNodes: PuppyAssemblyNode[]
  /** 装配节点索引。*/
  assemblyNodesById: Map<string, PuppyAssemblyNode>
  /** 父节点到子节点 ID 的索引。*/
  childIdsByNodeId: Record<string, string[]>
  /** assembly node 自增序列。*/
  assemblyNodeSeq: number
}

/**
 * 创建合成装配根节点。
 *
 * @param input 创建输入。
 * @returns 合成根节点 ID。
 */
function createSyntheticAssemblyRoot(input: {
  context: PuppyAssemblyDocumentBuildContext
  root: THREE.Group
  sceneRoot: THREE.Object3D
  topLevelNodeIds: string[]
  documentType?: PuppyPackageDocumentType
}): string {
  input.root.updateMatrix()
  const nodeId = 'assembly-root'
  if (input.context.assemblyNodesById.has(nodeId)) {
    throw new Error(`PuppyCAD 合成装配根身份与组件冲突：assemblyNodeId=${nodeId}`)
  }
  const name = input.sceneRoot.name?.trim() || 'PuppyCAD Offline Package'
  const syntheticRootSemanticKind: PuppyAssemblyNode['semanticKind'] = input.documentType === 'part'
    ? 'part'
    : 'assembly'
  const node: PuppyAssemblyNode = {
    nodeId,
    parentNodeId: null,
    name,
    nodeType: 'Group',
    path: name,
    selectable: input.topLevelNodeIds.length > 0,
    semanticKind: syntheticRootSemanticKind,
    sourceObjectId: input.sceneRoot.uuid,
    transform: toMatrixTuple(input.root.matrix),
    displayNodeIds: [],
    bodyBindingIds: [],
    geometryObjectIds: [],
  }

  input.topLevelNodeIds.forEach((childNodeId) => {
    const childNode = input.context.assemblyNodesById.get(childNodeId)
    if (childNode) {
      childNode.parentNodeId = nodeId
    }
  })
  input.context.assemblyNodes.unshift(node)
  input.context.assemblyNodesById.set(nodeId, node)
  input.context.childIdsByNodeId[nodeId] = [...input.topLevelNodeIds]
  input.root.userData = {
    ...input.root.userData,
    puppy: {
      assemblyNodeId: nodeId,
      assemblyNodeRole: 'node',
    },
  }
  return nodeId
}

/**
 * 创建离线包装配文档。
 *
 * @param input 创建输入。
 * @returns assembly.json 文档。
 */
export function createPuppyAssemblyDocument(input: {
  /** 装配构建上下文。*/
  context: PuppyAssemblyDocumentBuildContext
  /** GLB 导出根。*/
  root: THREE.Group
  /** 当前 Viewer 场景根。*/
  sceneRoot: THREE.Object3D
  /** 顶层装配节点 ID。*/
  topLevelNodeIds: string[]
  /** 当前装配体标准配合文档 JSON。*/
  mates?: AssemblyMateDocumentJSON
  /** 当前文档级基准数据。*/
  datumExtension?: PuppyDatumExtensionInput | null
  /** 当前离线包文档类型；用于区分多实体零件与装配体的合成根语义。*/
  documentType?: PuppyPackageDocumentType
}): PuppyAssemblyDocument {
  const rootNodeId = input.topLevelNodeIds.length === 1
    ? input.topLevelNodeIds[0]!
    : createSyntheticAssemblyRoot(input)
  const mates = input.mates ?? toAssemblyMateDocumentJSON(createAssemblyMateDocument({
    assemblyId: rootNodeId,
    /**
     * 没有 Mate 时生成的是规范空文档，不代表一次用户编辑。使用墙上时间会让相同场景
     * 连续保存产生不同 assembly.json，因此固定为协议零点；真实 Mate 文档仍由调用方传入。
     */
    now: 0,
  }))
  const datumExtension = createPuppyDatumExtension(input.datumExtension)

  return {
    version: PUPPY_PACKAGE_VERSION,
    rootNodeId,
    nodes: input.context.assemblyNodes,
    childIdsByNodeId: input.context.childIdsByNodeId,
    mates,
    extensions: writePuppyDatumExtensionToAssemblyExtensions({
      schema: 'puppy-assembly-v1',
    }, datumExtension),
  }
}
